import React from 'react';
import { Info, Plus, Pencil, Trash2, RefreshCw, Loader2, ArrowLeftRight, ChevronDown } from 'lucide-react';
import { Switch } from '@mui/material';
import { SectionHeader, CollapsiblePanel } from '../../components/common/SectionHeader';

const cardClass =
  'bg-white dark:bg-[#1C1C1E] rounded-2xl md:rounded-[28px] border border-gray-100 dark:border-white/5 shadow-sm overflow-hidden';

const TransferCleaningRulesCard = ({
  expanded,
  onToggleExpanded,
  showRuleInfo,
  onToggleRuleInfo,
  showRuleForm,
  onToggleRuleForm,
  ruleForm,
  ruleFormContent,
  rules,
  loadingRules,
  onRefresh,
  onToggleActive,
  onEditRule,
  onDeleteRule,
}) => (
  <div className={cardClass}>
    <button
      type="button"
      onClick={onToggleExpanded}
      className="w-full flex items-center gap-2 md:gap-3 px-3 md:px-6 py-4 md:py-5 text-left hover:bg-gray-50/80 dark:hover:bg-white/[0.03] transition-colors border-b border-transparent"
    >
      <div className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-blue-100 dark:bg-blue-900/30 shrink-0">
        <ArrowLeftRight size={18} className="text-blue-600 dark:text-blue-200 md:w-5 md:h-5" />
      </div>
      <div className="min-w-0 flex-1 text-left">
        <h3 className="text-sm md:text-lg font-semibold tracking-tight text-gray-900 dark:text-white">
          Transfer/Cleaning Rules
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Rules run in priority order; inactive rules are skipped.
        </p>
      </div>
      <ChevronDown
        size={18}
        className={`text-gray-400 shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
      />
    </button>

    {expanded && (
      <div className="px-3 md:px-6 pb-5 md:pb-6 space-y-4 border-t border-gray-100 dark:border-white/5 pt-4">
        <CollapsiblePanel
          icon={Info}
          title="Rule Writing Guide"
          expanded={showRuleInfo}
          onToggle={onToggleRuleInfo}
          color="bg-gray-100 dark:bg-white/10"
        >
          <div className="grid gap-2 sm:grid-cols-2">
            {[
              ['kod("101")', 'Returns only account 101'],
              ['kodAlt("101")', 'Returns all sub-accounts of 101'],
              ['hasSubAccountWithName(...)', 'Checks sub-account name match'],
              ['sumSubAccountBalance(...)', 'Sums sub-account balance'],
            ].map(([fn, desc]) => (
              <div
                key={fn}
                className="rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/5 px-3 py-2"
              >
                <code className="text-[11px] font-mono text-[#24299d] dark:text-[#818cf8] block mb-0.5">{fn}</code>
                <span className="text-[11px] text-gray-500 dark:text-gray-400">{desc}</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3 leading-relaxed">
            <span className="font-semibold text-gray-500 dark:text-gray-400">Example condition:</span>{' '}
            hasSubAccountWithName(&quot;101&quot;, &quot;2024 VADELI ÇEKLER&quot;)
          </p>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1 leading-relaxed">
            <span className="font-semibold text-gray-500 dark:text-gray-400">Example amount:</span>{' '}
            sumSubAccountBalance(&quot;101&quot;, &quot;2024 VADELI ÇEKLER&quot;, &quot;alacak&quot;)
          </p>
        </CollapsiblePanel>

        <CollapsiblePanel
          icon={Plus}
          title={ruleForm?.id ? 'Edit Rule' : 'Add New Rule'}
          expanded={showRuleForm}
          onToggle={onToggleRuleForm}
          color="bg-green-100 dark:bg-green-900/30"
        >
          {ruleFormContent}
        </CollapsiblePanel>

        <div className="rounded-xl border border-gray-100 dark:border-white/5 bg-white dark:bg-[#1C1C1E] overflow-hidden shadow-sm">
          <div className="px-3 md:px-4 py-3 md:py-4">
            <SectionHeader
              icon={ArrowLeftRight}
              label="Rule List"
              className="mb-0"
              color="bg-purple-100 dark:bg-purple-900/30"
              right={
                <button
                  type="button"
                  onClick={onRefresh}
                  disabled={loadingRules}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-white/10 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
                >
                  {loadingRules ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  Refresh
                </button>
              }
            />
          </div>

          <div className="border-t border-gray-100 dark:border-white/5 overflow-hidden">
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <table className="w-full text-xs md:text-sm min-w-[720px]">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-gray-50 dark:bg-[#2C2C2E] border-b border-gray-100 dark:border-white/10">
                    {['Priority', 'Source', 'Target', 'Condition', 'Amount', 'Active', 'Actions'].map((col) => (
                      <th
                        key={col}
                        className={`px-4 py-3 font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap ${
                          col === 'Actions' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loadingRules ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8">
                        <div className="flex justify-center">
                          <Loader2 size={24} className="animate-spin text-[#24299d]" />
                        </div>
                      </td>
                    </tr>
                  ) : rules.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">
                        No rules defined.
                      </td>
                    </tr>
                  ) : (
                    rules.map((rule) => (
                      <tr
                        key={rule.id}
                        className="border-t border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors"
                      >
                        <td className="px-4 py-3 font-mono text-gray-700 dark:text-gray-200">{rule.oncelik}</td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-800 dark:text-gray-200">{rule.aktaranKod}</div>
                          <div className="text-[11px] text-gray-400">{rule.aktaranYon}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-800 dark:text-gray-200">{rule.aktarilanKod}</div>
                          <div className="text-[11px] text-gray-400">{rule.aktarilanYon}</div>
                        </td>
                        <td className="px-4 py-3 max-w-[200px] whitespace-normal break-words text-gray-600 dark:text-gray-300">
                          {rule.kosul}
                        </td>
                        <td className="px-4 py-3 max-w-[200px] whitespace-normal break-words text-gray-600 dark:text-gray-300">
                          {rule.tutar}
                        </td>
                        <td className="px-4 py-3">
                          <Switch size="small" checked={rule.aktif} onChange={() => onToggleActive(rule)} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => onEditRule(rule)}
                              className="p-1.5 rounded-lg bg-gray-100 dark:bg-white/10 hover:bg-amber-100 dark:hover:bg-amber-900/30 text-gray-500 hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => onDeleteRule(rule.id)}
                              className="p-1.5 rounded-lg bg-gray-100 dark:bg-white/10 hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-500 hover:text-red-600 transition-colors"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);

export default TransferCleaningRulesCard;
